// src/components/settings/InstagramOnboardingWizard.jsx - Updated for 2025 Facebook process
import React, { useState } from 'react';
import { useMutation } from 'react-query';
import {
    ClipboardDocumentIcon,
    LinkIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ArrowRightIcon,
    ArrowLeftIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import { settingsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const STEPS = {
    WELCOME: 'welcome',
    REQUIREMENTS: 'requirements',
    CREATE_APP: 'create_app',
    CONFIGURE_APP: 'configure_app',
    GET_ACCESS_TOKEN: 'get_access_token',
    ENTER_CREDENTIALS: 'enter_credentials',
    TEST_CONNECTION: 'test_connection',
    COMPLETE: 'complete'
};

function InstagramOnboardingWizard({ onComplete, onClose, onBack }) {
    const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
    const [credentials, setCredentials] = useState({
        accessToken: '',
        businessAccountId: ''
    });
    const [testResult, setTestResult] = useState(null);

    // Save credentials mutation
    const saveCredentialsMutation = useMutation(settingsAPI.saveCredentials, {
        onSuccess: () => {
            toast.success('Instagram credentials saved!');
            setCurrentStep(STEPS.TEST_CONNECTION);
        },
        onError: (error) => {
            toast.error('Failed to save credentials: ' + error.message);
        }
    });

    // Test Instagram connection mutation
    const testInstagramMutation = useMutation(settingsAPI.testInstagram, {
        onSuccess: (response) => {
            setTestResult(response.data);
            setCurrentStep(STEPS.COMPLETE);
            toast.success('Instagram connection test successful!');
        },
        onError: (error) => {
            toast.error('Connection test failed: ' + error.message);
            setTestResult({ error: error.message });
        }
    });

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const getCurrentBaseUrl = () => {
        return `${window.location.protocol}//${window.location.host}`;
    };

    const handleNext = () => {
        const stepOrder = Object.values(STEPS);
        const currentIndex = stepOrder.indexOf(currentStep);
        if (currentIndex < stepOrder.length - 1) {
            setCurrentStep(stepOrder[currentIndex + 1]);
        }
    };

    const handleBack = () => {
        const stepOrder = Object.values(STEPS);
        const currentIndex = stepOrder.indexOf(currentStep);
        if (currentIndex > 0) {
            setCurrentStep(stepOrder[currentIndex - 1]);
        }
    };

    const handleSaveCredentials = () => {
        if (!credentials.accessToken.trim() || !credentials.businessAccountId.trim()) {
            toast.error('Please fill in both access token and business account ID');
            return;
        }

        saveCredentialsMutation.mutate({
            INSTAGRAM_ACCESS_TOKEN: credentials.accessToken.trim(),
            INSTAGRAM_BUSINESS_ACCOUNT_ID: credentials.businessAccountId.trim()
        });
    };

    const handleTestConnection = () => {
        testInstagramMutation.mutate();
    };

    const handleComplete = () => {
        onComplete?.();
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case STEPS.WELCOME:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="text-6xl mb-4">📋</div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Manual Instagram Setup Guide
                            </h2>
                            <p className="text-gray-600 max-w-md mx-auto">
                                We'll walk you through connecting your Instagram Business account step-by-step
                                using the latest Facebook Developer process.
                            </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">Updated for 2025:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Uses latest Facebook App creation process</li>
                                        <li>Updated Instagram Graph API v19.0</li>
                                        <li>New Meta Developer Console interface</li>
                                        <li>Simplified permission requirements</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">Requirements (unchanged):</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Instagram Business or Creator account</li>
                                        <li>Connected to a Facebook Page</li>
                                        <li>Meta Developer account</li>
                                        <li>Admin access to the Facebook Page</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                >
                                    ← Automated Setup
                                </button>
                            )}
                            <div className="flex-1" />
                            <button
                                onClick={handleNext}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Start Manual Setup
                            </button>
                        </div>
                    </div>
                );

            case STEPS.REQUIREMENTS:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Account Requirements Check
                            </h2>
                            <p className="text-gray-600">
                                Verify your accounts meet the requirements for Instagram API access.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                                    <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">1</span>
                                    Instagram Business Account
                                </h3>
                                <div className="ml-8 space-y-2 text-sm text-gray-600">
                                    <p>✓ Must be Business or Creator account (not Personal)</p>
                                    <p>✓ Account must be active and in good standing</p>
                                    <div className="bg-blue-50 p-3 rounded mt-2">
                                        <p className="font-medium text-blue-800 mb-1">To convert to Business:</p>
                                        <ol className="list-decimal list-inside space-y-1 text-blue-700">
                                            <li>Instagram app → Profile → Menu (≡) → Settings and privacy</li>
                                            <li>Account type and tools → Switch to professional account</li>
                                            <li>Choose "Business" → Complete setup</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                                    <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">2</span>
                                    Facebook Page Connection
                                </h3>
                                <div className="ml-8 space-y-2 text-sm text-gray-600">
                                    <p>✓ Instagram account connected to Facebook Page</p>
                                    <p>✓ You must be admin of the Facebook Page</p>
                                    <div className="bg-blue-50 p-3 rounded mt-2">
                                        <p className="font-medium text-blue-800 mb-1">To connect accounts:</p>
                                        <ol className="list-decimal list-inside space-y-1 text-blue-700">
                                            <li>Facebook Page → Settings → Instagram</li>
                                            <li>Connect Account → Log into Instagram</li>
                                            <li>Authorize connection</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                                    <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">3</span>
                                    Meta Developer Account
                                </h3>
                                <div className="ml-8 space-y-2 text-sm text-gray-600">
                                    <p>✓ Meta Developer account (free)</p>
                                    <p>✓ Phone number verified</p>
                                    <a
                                        href="https://developers.facebook.com/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-blue-600 hover:text-blue-500 mt-2"
                                    >
                                        Create Meta Developer Account
                                        <LinkIcon className="ml-1 h-4 w-4" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case STEPS.CREATE_APP:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Create Meta App (Updated Process)
                            </h2>
                            <p className="text-gray-600">
                                Create a new app in the Meta Developer Console using the latest interface.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">1</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Go to Meta for Developers
                                        </h3>
                                        <a
                                            href="https://developers.facebook.com/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                        >
                                            Open Meta Developers
                                            <LinkIcon className="ml-2 h-4 w-4" />
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">2</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Create New App
                                        </h3>
                                        <div className="text-sm text-gray-600 space-y-2">
                                            <p>Click "Create App" → Choose "Business" use case</p>
                                            <div className="bg-gray-50 p-3 rounded">
                                                <p className="font-medium mb-2">App Details:</p>
                                                <div className="space-y-1">
                                                    <div><strong>App name:</strong> PostMgr Instagram Connect</div>
                                                    <div><strong>Contact email:</strong> Your email</div>
                                                    <div><strong>Purpose:</strong> Social media management</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">3</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Add Instagram Graph API
                                        </h3>
                                        <div className="text-sm text-gray-600 space-y-2">
                                            <p>In your app dashboard:</p>
                                            <ol className="list-decimal list-inside ml-4 space-y-1">
                                                <li>Go to "Add Products"</li>
                                                <li>Find "Instagram Graph API"</li>
                                                <li>Click "Set up"</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case STEPS.CONFIGURE_APP:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Configure Instagram API Settings
                            </h2>
                            <p className="text-gray-600">
                                Set up your app permissions and connect your Instagram account.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">1</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Configure Basic Settings
                                        </h3>
                                        <div className="text-sm text-gray-600 space-y-2">
                                            <p>Go to Instagram Graph API → Settings → Basic:</p>
                                            <div className="bg-gray-50 p-3 rounded border">
                                                <p className="font-medium mb-2">Add Valid OAuth Redirect URI:</p>
                                                <div className="flex items-center justify-between bg-white p-2 rounded border">
                                                    <code className="text-sm font-mono text-gray-800">
                                                        {getCurrentBaseUrl()}/auth/instagram/callback
                                                    </code>
                                                    <button
                                                        onClick={() => copyToClipboard(`${getCurrentBaseUrl()}/auth/instagram/callback`)}
                                                        className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                                                        title="Copy to clipboard"
                                                    >
                                                        <ClipboardDocumentIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">2</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Add Instagram Account
                                        </h3>
                                        <div className="text-sm text-gray-600 space-y-2">
                                            <p>In Instagram Graph API settings:</p>
                                            <ol className="list-decimal list-inside ml-4 space-y-1">
                                                <li>Click "Add or Remove Instagram Accounts"</li>
                                                <li>Log in to Instagram</li>
                                                <li>Select your Business account</li>
                                                <li>Grant all requested permissions</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">3</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Note Your App Credentials
                                        </h3>
                                        <div className="text-sm text-gray-600 space-y-2">
                                            <p>Go to App Settings → Basic and copy:</p>
                                            <ul className="list-disc list-inside ml-4 space-y-1">
                                                <li><strong>App ID</strong></li>
                                                <li><strong>App Secret</strong></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

            );

            case STEPS.GET_ACCESS_TOKEN:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Generate Access Token & Webhook Setup
                            </h2>
                            <p className="text-gray-600">
                                Create your access token and configure webhooks (optional for PostMgr).
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">1</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Generate Access Token
                                        </h3>
                                        <div className="text-sm text-gray-600 space-y-2">
                                            <p>In the "Generate access tokens" section:</p>
                                            <ol className="list-decimal list-inside space-y-1">
                                                <li>Click "Generate token" for your Instagram account</li>
                                                <li>Log in to Instagram if prompted</li>
                                                <li>Grant all requested permissions</li>
                                                <li>Copy the generated token</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-orange-600 font-medium text-sm">2</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Configure Webhooks (Optional for PostMgr)
                                        </h3>
                                        <div className="text-sm text-gray-600 space-y-2">
                                            <div className="bg-orange-50 p-3 rounded border border-orange-200">
                                                <p className="font-medium text-orange-800 mb-1">📢 Important Note:</p>
                                                <p className="text-orange-700">
                                                    Webhooks are <strong>optional</strong> for PostMgr. They're used for receiving real-time notifications
                                                    (comments, messages), but PostMgr primarily posts content. You can skip this step if you want.
                                                </p>
                                            </div>

                                            <p className="font-medium">If you want to configure webhooks:</p>
                                            <div className="ml-4 space-y-1">
                                                <p><strong>Callback URL:</strong> Your public domain + /webhooks/instagram</p>
                                                <p><strong>Verify Token:</strong> Any random string (e.g., "myPostMgrWebhook123")</p>
                                                <p><strong>Requirements:</strong> App must be in "Live" mode, publicly accessible URL</p>
                                            </div>

                                            <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                                <p className="text-blue-800 text-sm">
                                                    💡 For development: Use ngrok or similar to expose localhost publicly.
                                                    For production: Use your actual domain.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">3</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Create Long-Lived Token (Recommended)
                                        </h3>
                                        <div className="text-sm text-gray-600 space-y-2">
                                            <p>The token from step 1 is short-lived. For a 60-day token:</p>
                                            <ol className="list-decimal list-inside space-y-1">
                                                <li>Go to <a href="https://developers.facebook.com/tools/debug/accesstoken/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500">Access Token Debugger</a></li>
                                                <li>Paste your token and click "Debug"</li>
                                                <li>Click "Extend Access Token"</li>
                                                <li>Copy the new long-lived token (60 days)</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">4</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Find Business Account ID
                                        </h3>
                                        <div className="text-sm text-gray-600 space-y-2">
                                            <p>Use Graph API Explorer to find your Instagram Business Account ID:</p>
                                            <ol className="list-decimal list-inside space-y-1">
                                                <li>Open <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500">Graph API Explorer</a></li>
                                                <li>Paste your long-lived token</li>
                                                <li>Request: <code className="bg-gray-100 px-1 rounded">me/accounts</code></li>
                                                <li>Find your Facebook Page ID in the response</li>
                                                <li>Request: <code className="bg-gray-100 px-1 rounded">[PAGE_ID]?fields=instagram_business_account</code></li>
                                                <li>Copy the instagram_business_account.id</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case STEPS.ENTER_CREDENTIALS:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Enter Your Instagram Credentials
                            </h2>
                            <p className="text-gray-600">
                                Paste the access token and business account ID you obtained.
                            </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">📋 App Review Status:</p>
                                    <p>
                                        Your app works in <strong>Development Mode</strong> immediately with your account.
                                        For production use with other users, complete the app review process later.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="accessToken" className="block text-sm font-medium text-gray-700 mb-1">
                                    Long-Lived Access Token
                                </label>
                                <textarea
                                    id="accessToken"
                                    value={credentials.accessToken}
                                    onChange={(e) => setCredentials(prev => ({ ...prev, accessToken: e.target.value.trim() }))}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Paste your long-lived access token here"
                                    rows={3}
                                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                                />
                                <p className="mt-1 text-sm text-gray-500">
                                    From the "Generate access tokens" section (should be ~255 characters)
                                </p>
                                {credentials.accessToken && (
                                    <p className="mt-1 text-xs text-gray-400">
                                        Length: {credentials.accessToken.length} characters
                                    </p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="businessAccountId" className="block text-sm font-medium text-gray-700 mb-1">
                                    Instagram Business Account ID
                                </label>
                                <input
                                    type="text"
                                    id="businessAccountId"
                                    value={credentials.businessAccountId}
                                    onChange={(e) => setCredentials(prev => ({ ...prev, businessAccountId: e.target.value.trim() }))}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="178414749499976680"
                                    style={{ fontFamily: 'monospace' }}
                                />
                                <p className="mt-1 text-sm text-gray-500">
                                    The numeric ID from the Generate access tokens section
                                </p>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">🚀 Optional Next Steps (for production):</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li><strong>Instagram Business Login:</strong> Optional for user authentication</li>
                                        <li><strong>App Review:</strong> Required for live access beyond your account</li>
                                        <li><strong>Development Mode:</strong> Works immediately for testing</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleSaveCredentials}
                            disabled={!credentials.accessToken.trim() || !credentials.businessAccountId.trim() || saveCredentialsMutation.isLoading}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saveCredentialsMutation.isLoading ? 'Saving...' : 'Save Credentials & Test Connection'}
                        </button>
                    </div>
                );

            case STEPS.TEST_CONNECTION:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Test Instagram Connection
                            </h2>
                            <p className="text-gray-600">
                                Let's verify that your Instagram integration is working correctly.
                            </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                                <div className="text-sm text-green-800">
                                    <p className="font-medium">Credentials Saved Successfully</p>
                                    <p>Your Instagram access token and business account ID have been saved securely.</p>
                                </div>
                            </div>
                        </div>

                        <div className="text-center">
                            <button
                                onClick={handleTestConnection}
                                disabled={testInstagramMutation.isLoading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {testInstagramMutation.isLoading ? (
                                    <>
                                        <img src="/images/Bob_only.svg" alt="PostMgr" className="h-4 w-4 animate-spin" />
                                        Testing Connection...
                                    </>
                                ) : (
                                    'Test Instagram Connection'
                                )}
                            </button>
                        </div>

                        {testResult?.error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="text-red-800">
                                    <p className="font-medium">Connection Test Failed</p>
                                    <p className="text-sm mt-1">{testResult.error}</p>
                                </div>
                            </div>
                        )}
                    </div>
                );

            case STEPS.COMPLETE:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="text-6xl mb-4">🎉</div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Instagram Setup Complete!
                            </h2>
                            <p className="text-gray-600">
                                Your Instagram Business account is now connected to PostMgr.
                            </p>
                        </div>

                        {testResult && !testResult.error && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="text-sm text-green-800 space-y-2">
                                    <p className="font-medium">✅ Connection Test Successful</p>
                                    {testResult.accountInfo && (
                                        <div className="bg-white p-3 rounded border border-green-300">
                                            <p><strong>Account:</strong> @{testResult.accountInfo.username}</p>
                                            <p><strong>Name:</strong> {testResult.accountInfo.name}</p>
                                            <p><strong>Followers:</strong> {testResult.accountInfo.followers?.toLocaleString()}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="text-sm text-blue-800">
                                <p className="font-medium mb-2">What's next:</p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Enable Instagram in your Platform Settings</li>
                                    <li>Create and publish posts to Instagram</li>
                                    <li>Monitor your posting performance</li>
                                    <li>Set up scheduling for automated posting</li>
                                </ul>
                            </div>
                        </div>

                        <div className="text-center">
                            <button
                                onClick={handleComplete}
                                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                Complete Setup
                            </button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const canProceedToNext = () => {
        switch (currentStep) {
            case STEPS.ENTER_CREDENTIALS:
                return credentials.accessToken.trim() && credentials.businessAccountId.trim();
            case STEPS.TEST_CONNECTION:
                return false; // Handled by specific button
            case STEPS.COMPLETE:
                return false; // Handled by specific button
            default:
                return true;
        }
    };

    const getStepNumber = (step) => {
        const steps = Object.values(STEPS);
        return steps.indexOf(step) + 1;
    };

    const getTotalSteps = () => {
        return Object.values(STEPS).length;
    };

    return (
        <div className="space-y-6">
            {/* Progress bar */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg">
                <div className="flex items-center justify-between mb-2">
                    <h1 className="text-lg font-medium text-gray-900">Manual Instagram Setup</h1>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                    )}
                </div>

                <div className="flex items-center space-x-2">
                    {Object.values(STEPS).map((step, index) => (
                        <React.Fragment key={step}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                                currentStep === step
                                    ? 'bg-blue-600 text-white'
                                    : index < Object.values(STEPS).indexOf(currentStep)
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-200 text-gray-600'
                            }`}>
                                {index < Object.values(STEPS).indexOf(currentStep) ? '✓' : index + 1}
                            </div>
                            {index < Object.values(STEPS).length - 1 && (
                                <div className={`flex-1 h-1 rounded ${
                                    index < Object.values(STEPS).indexOf(currentStep) ? 'bg-green-500' : 'bg-gray-200'
                                }`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="px-6">
                {renderStepContent()}
            </div>

            {/* Footer */}
            {currentStep !== STEPS.TEST_CONNECTION && currentStep !== STEPS.COMPLETE && (
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200 rounded-b-lg">
                    <button
                        onClick={handleBack}
                        disabled={currentStep === STEPS.WELCOME}
                        className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                            currentStep === STEPS.WELCOME
                                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back
                    </button>

                    <div className="text-sm text-gray-500">
                        Step {getStepNumber(currentStep)} of {getTotalSteps()}
                    </div>

                    {currentStep !== STEPS.ENTER_CREDENTIALS ? (
                        <button
                            onClick={handleNext}
                            disabled={!canProceedToNext()}
                            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                                !canProceedToNext()
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            Next
                            <ArrowRightIcon className="h-4 w-4 ml-2" />
                        </button>
                    ) : (
                        <div></div>
                    )}
                </div>
            )}
        </div>
    );
}

export default InstagramOnboardingWizard;